<article class="article">
  <main>
    <div class="content">
      <a
        matButton
        class="primary-button back-btn"
        [routerLink]="['/', 'provider', 'admin', 'services']">
        <mat-icon>chevron_left</mat-icon>
        Back to list
      </a>

      <div class="header-container">
        <div class="visibleTo-container">
          <h4>Visible to:</h4>
          @for (offering of service.visibleTo; let last = $last; track offering) {
            <span>{{ offering }}{{ last ? ';' : ',&nbsp;' }}</span>
          } @empty {
            <span>-</span>
          }
        </div>

        @if (serviceForm.get(fields.paidService)?.value) {
          <ume-alert
            [iconLigature]="'info'"
            [color]="'caution'"
            [text]="'<p>Automatic activation type is unavailable for the paid service</p>'"></ume-alert>
        }

        @if (serviceForm.get(fields.activationType)?.value === activationTypes.AUTOMATIC) {
          <ume-alert
            [iconLigature]="'info'"
            [color]="'caution'"
            [text]="'<p>Paid service option is unavailable for automatic activation type</p>'"></ume-alert>
        }
      </div>

      <form
        class="services-form"
        [formGroup]="serviceForm">
        <div class="flex align-center flex-gap nowrap m-b">
          <div
            class="extra-indent"
            matTooltip="Upload svg image">
            <input
              #imgUpload
              type="file"
              hidden
              (change)="fileChangeEvent($event)" />
            <button
              matIconButton
              class="primary-button"
              [disabled]="!canEdit"
              (click)="imgUpload.click()">
              <mat-icon
                matPrefix
                fontSet="material-symbols-outlined">
                upload
              </mat-icon>
            </button>
          </div>

          @if (serviceForm.get(fields.image)?.value) {
            <div class="img-preview extra-indent">
              <picture>
                <img
                  [src]="serviceForm.get(fields.image)?.value | sanitizeSrc"
                  [alt]="'Service icon'" />
              </picture>
              <button
                matIconButton
                [disabled]="!canEdit"
                (click)="clearFieldValue(fields.image)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }

          <mat-form-field appearance="outline">
            <mat-label>Activation of the service</mat-label>
            <mat-select [formControlName]="fields.activationType">
              <mat-option [value]="activationTypes.MANUAL">{{ activationTypes.MANUAL }}</mat-option>
              <mat-option
                [value]="activationTypes.AUTOMATIC"
                [disabled]="serviceForm.get(fields.paidService)?.value">
                {{ activationTypes.AUTOMATIC }}
              </mat-option>
            </mat-select>
            @if (serviceForm.get(fields.activationType)?.invalid) {
              <mat-error>Activation type is required</mat-error>
            }
          </mat-form-field>

          <mat-form-field
            class="w-50"
            appearance="outline">
            <mat-label>Name</mat-label>
            <input
              matInput
              [formControlName]="fields.name"
              [name]="fields.name" />
            @if (serviceForm.get(fields.name)?.invalid) {
              <mat-error>Name is required</mat-error>
            }
          </mat-form-field>

          <mat-slide-toggle
            class="no-validation extra-indent"
            [formControlName]="fields.paidService"
            [name]="fields.paidService">
            The service is paid
          </mat-slide-toggle>

          <mat-form-field
            class="no-validation"
            appearance="outline">
            <mat-label>Service fee(EUR)</mat-label>
            <input
              matInput
              type="number"
              [min]="0"
              [formControlName]="fields.serviceFee"
              [name]="fields.serviceFee" />
          </mat-form-field>

          <mat-slide-toggle
            class="no-validation extra-indent"
            [formControlName]="fields.isHidden"
            [name]="fields.isHidden">
            Hide from everyone
          </mat-slide-toggle>
        </div>

        <div class="editors-container">
          <div class="editor-container">
            <h5>Description</h5>
            @defer (on viewport) {
              <ume-ck-editor-field
                [id]="'service-configuration-description-text'"
                [formControlName]="fields.description"
                [height]="240"
                [debounceTime]="600"></ume-ck-editor-field>
            } @placeholder (minimum 500ms) {
              <div class="editor-skeleton">
                <mat-progress-spinner
                  [diameter]="32"
                  [mode]="'indeterminate'"></mat-progress-spinner>
                <p>Loading rich text editor...</p>
              </div>
            } @loading {
              <div class="editor-skeleton">
                <p>Loading rich text editor...</p>
              </div>
            } @error {
              <div class="error-msg">Failed to load the text editor. Please refresh the page.</div>
            }
          </div>

          <div class="editor-container benefits-container">
            <h5>Benefits</h5>
            @defer (on viewport) {
              <ume-ck-editor-field
                [formControlName]="fields.benefits"
                [height]="240"
                [debounceTime]="600"></ume-ck-editor-field>
            } @placeholder (minimum 500ms) {
              <div class="editor-skeleton">
                <mat-progress-spinner
                  [diameter]="32"
                  [mode]="'indeterminate'"></mat-progress-spinner>
                <p>Loading rich text editor...</p>
              </div>
            } @loading {
              <div class="editor-skeleton">
                <p>Loading rich text editor...</p>
              </div>
            } @error {
              <div class="error-msg">Failed to load the text editor. Please refresh the page.</div>
            }
          </div>
        </div>
      </form>

      <div
        [style.justify-content]="service.isDraft ? 'space-between' : 'flex-end'"
        class="actions">
        @if (service.isDraft) {
          <ume-alert
            [iconLigature]="'info'"
            [color]="'caution'"
            [text]="'<p>You have unsaved changes. Are you sure you want to leave?</p>'"></ume-alert>
        }

        <div class="flex flex-gap">
          <button
            matButton
            class="primary-button"
            [disabled]="!service.isDraft"
            (click)="undoChanges()">
            <mat-icon>undo</mat-icon>
            Undo
          </button>

          <button
            matButton="filled"
            class="primary-button"
            [disabled]="!service.isDraft || !serviceForm.valid"
            (click)="publishChanges()">
            publish
          </button>
        </div>
      </div>

      <mat-divider></mat-divider>

      <ume-terms-and-conditions
        [offeringId]="offeringId"
        [linkedTermsAndConditions]="terms?.linkedTermsAndConditions ?? []"
        [notLinkedTermsAndConditions]="terms?.notLinkedTermsAndConditions ?? []"
        (refresh)="refreshInfo()"></ume-terms-and-conditions>
    </div>
  </main>
</article>
